import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight, ArrowUpRight, MessageSquare, CheckCircle2, Users, Wallet,
  Megaphone, FilePlus2, Plus, TrendingUp,
} from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";

interface DashboardData {
  metrics: {
    messages_today: number;
    delivery_rate: number;
    active_conversations: number;
    balance: number;
  };
  channels: Array<{
    id: number;
    name: string;
    phone: string;
    quality: string;
    tier: string;
    status: string;
  }>;
  recent_messages: Array<{
    id: number;
    direction: string;
    recipient: string;
    status: string;
    content: string | { body?: string };
    created_at: string;
  }>;
}

function StatCard({
  label, value, icon: Icon, suffix,
}: { label: string; value: string; icon: any; suffix?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tracking-tight">{value}</span>
        {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => apiFetch<DashboardData>("/api/dashboard"),
  });

  const metrics = data?.metrics ?? { messages_today: 0, delivery_rate: 0, active_conversations: 0, balance: 0 };
  const channels = data?.channels ?? [];
  const recentMessages = data?.recent_messages ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Overview of your messaging activity, channel health and balance."
        actions={
          <>
            <Button variant="outline" size="sm" asChild><Link to="/app/templates"><FilePlus2 className="h-4 w-4" />New template</Link></Button>
            <Button size="sm" asChild><Link to="/app/broadcasts"><Megaphone className="h-4 w-4" />New broadcast</Link></Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Messages today" value={metrics.messages_today.toLocaleString()} icon={MessageSquare} />
        <StatCard label="Delivery rate" value={`${metrics.delivery_rate}%`} icon={CheckCircle2} />
        <StatCard label="Active conversations" value={metrics.active_conversations.toString()} icon={Users} />
        <StatCard label="Account balance" value={`$${metrics.balance.toLocaleString()}`} icon={Wallet} suffix="USD" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="text-sm font-semibold">Channel health</h2>
            <Link to="/app/channels" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {channels.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">
              No channels connected yet.{" "}
              <Link to="/app/channels" className="text-primary hover:underline">Connect WhatsApp</Link>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {channels.map((ch) => (
                <div key={ch.id} className="grid grid-cols-12 items-center gap-4 px-5 py-4">
                  <div className="col-span-12 md:col-span-5">
                    <div className="font-medium text-sm">{ch.name}</div>
                    <div className="text-xs text-muted-foreground">{ch.phone}</div>
                  </div>
                  <div className="col-span-4 md:col-span-3"><StatusBadge status={ch.quality} /></div>
                  <div className="col-span-4 md:col-span-2 text-xs text-muted-foreground">Tier {ch.tier}</div>
                  <div className="col-span-4 md:col-span-2 text-xs text-muted-foreground capitalize">{ch.status}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="text-sm font-semibold">Recent activity</h2>
            <Link to="/app/messages" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              All messages <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {recentMessages.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">
              No messages yet.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {recentMessages.map((m) => (
                <li key={m.id} className="flex items-start gap-3 px-5 py-3">
                  <div className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${m.direction === "outbound" ? "bg-primary/10 text-primary" : "bg-info/10 text-info"}`}>
                    {m.direction === "outbound" ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowRight className="h-3.5 w-3.5 rotate-180" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs font-medium">{m.recipient}</span>
                      <StatusBadge status={m.status} className="shrink-0" />
                    </div>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">{typeof m.content === "string" ? m.content : m.content?.body ?? ""}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <h2 className="text-sm font-semibold">Quick actions</h2>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            { label: "New broadcast", desc: "Send a template to many contacts", icon: Megaphone, to: "/app/broadcasts" },
            { label: "New template", desc: "Compose and submit to Meta", icon: FilePlus2, to: "/app/templates" },
            { label: "Top up balance", desc: "Add credit to your account", icon: Plus, to: "/app/billing" },
          ].map((q) => (
            <Link key={q.label} to={q.to} className="group flex items-start gap-3 rounded-md border border-border p-4 transition-colors hover:border-primary/40 hover:bg-primary/5">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary"><q.icon className="h-4 w-4" /></div>
              <div className="min-w-0">
                <div className="text-sm font-medium">{q.label}</div>
                <div className="text-xs text-muted-foreground">{q.desc}</div>
              </div>
              <ArrowRight className="ml-auto mt-2 h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
